import React, { useRef, useState, useEffect, memo, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import { FiTrendingUp, FiTrendingDown } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { Skeleton } from "antd";
import { useTranslation } from "react-i18next";
import { fetchIncomesGraph } from "../../../../Store/Slices/Finance/Earnings/earningsThunks";

ChartJS.register(...registerables);

const BarGraphSkeleton = () => {
  const bars = Array.from({ length: 10 });
  return (
    <div className="flex items-end space-x-2 h-72">
      {bars.map((_, index) => (
        <div
          key={index}
          className="w-6 bg-gray-200 rounded animate-pulse"
          style={{ height: `${Math.floor(Math.random() * 60) + 40}%` }}
        />
      ))}
    </div>
  );
};

const ChartTooltip = ({ tooltipData, schoolCurrency }) => (
  <div
    style={{
      position: "absolute",
      left: tooltipData.left,
      top: tooltipData.top,
      transform: "translate(-50%, -100%)",
      backgroundColor: "rgba(0,0,0,0.8)",
      color: "#fff",
      padding: "8px",
      borderRadius: "4px",
      pointerEvents: "none",
    }}
  >
    <div>
      {tooltipData.value} {schoolCurrency}
    </div>
    <div>{tooltipData.label}</div>
  </div>
);

const TotalEarningsGraph = ({ isOverview = false }) => {
  const schoolCurrency = useSelector(
    (store) => store.common.user.userDetails?.currency,
  );
  const chartRef = useRef(null);
  const [tooltipData, setTooltipData] = useState(null);
  const [selectedOption, setSelectedOption] = useState("currentMonth");

  const { t } = useTranslation("dashboard");
  const dispatch = useDispatch();
  const { incomeGraphData, loading, error } = useSelector(
    (state) => state.admin.earnings,
  );

  useEffect(() => {
    dispatch(fetchIncomesGraph());
  }, [dispatch]);

  const groupedData = useMemo(() => {
    return incomeGraphData.reduce((acc, { time, totalRevenue, category }) => {
      if (!acc[time]) acc[time] = { Earning: 0, Expense: 0 };
      acc[time][category] += totalRevenue;
      return acc;
    }, {});
  }, [incomeGraphData]);

  const totalEarnings = useMemo(
    () =>
      Object.values(groupedData).reduce((sum, data) => sum + data.Earning, 0),
    [groupedData],
  );
  const totalExpenses = useMemo(
    () =>
      Object.values(groupedData).reduce((sum, data) => sum + data.Expense, 0),
    [groupedData],
  );

  const chartData = {
    labels: Object.keys(groupedData),
    fill: true,
    datasets: [
      {
        label: t("Earnings"),
        data: Object.values(groupedData).map((data) => data.Earning),
        borderColor: "#7C3AED",
        backgroundColor: "rgba(124, 58, 237, 0.1)",
        borderWidth: 2,
        tension: 0.4,
        pointBackgroundColor: "#7C3AED",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: t("Expenses"),
        data: Object.values(groupedData).map((data) => data.Expense),
        borderColor: "#EA580C",
        borderDash: [5, 5],
        backgroundColor: "rgba(234, 88, 12, 0.08)",
        borderWidth: 2,
        tension: 0.4,
        pointBackgroundColor: "#EA580C",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        enabled: false,
        external: (context) => {
          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            setTooltipData(null);
            return;
          }
          const value = tooltipModel?.dataPoints[0].raw?.toLocaleString();
          const label = tooltipModel?.dataPoints[0]?.label;
          setTooltipData({
            value,
            label,
            left: tooltipModel.caretX,
            top: tooltipModel.caretY,
          });
        },
      },
      legend: { display: true },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (value) => value?.toLocaleString() },
        grid: { display: false },
      },
      x: {
        grid: {
          display: true,
          drawBorder: false,
          color: "rgba(0,0,0,0.06)",
          borderDash: [5, 5],
        },
      },
    },
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4 min-h-[400px]">
        <div className="flex justify-between items-center">
          <Skeleton.Input active style={{ width: 180, height: 20 }} />
          <Skeleton.Input active style={{ width: 80, height: 24 }} />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <BarGraphSkeleton />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Skeleton.Input
            active
            style={{ width: "100%", height: 60, borderRadius: 12 }}
          />
          <Skeleton.Input
            active
            style={{ width: "100%", height: 60, borderRadius: 12 }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 min-h-[400px] w-full bg-white p-4 rounded-xl shadow-sm">
      {/* ── Header ──────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-base font-bold text-gray-900">
            {t("Revenue vs Expenses Trend")}
          </h2>
          <p className="text-xs text-red-400 font-medium mt-0.5">
            ↗ Revenue trend{" "}
            <span className="text-red-500 font-bold">based on your data</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 font-medium">2025</span>
          <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-700">
            Monthly
          </span>
        </div>
      </div>

      {/* ── Chart ───────────────────────────────── */}
      <div className="relative flex-1" style={{ height: "280px" }}>
        <Line ref={chartRef} data={chartData} options={options} />
        {tooltipData && (
          <ChartTooltip
            tooltipData={tooltipData}
            schoolCurrency={schoolCurrency}
          />
        )}
      </div>

      {/* ── Bottom stat cards ────────────────────── */}
      {isOverview ? (
        ""
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {/* Revenue */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
              <FiTrendingUp className="w-4 h-4 text-gray-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                {t("Revenue")}
              </p>
              <p className="text-sm font-bold text-gray-900 truncate">
                {totalEarnings?.toLocaleString()} {schoolCurrency}
              </p>
            </div>
          </div>

          {/* Expenses */}

          <div className="bg-red-50 rounded-2xl border border-red-100 shadow-sm p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
              <FiTrendingDown className="w-4 h-4 text-red-500" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-widest text-red-400">
                {t("Expenses")}
              </p>
              <p className="text-sm font-bold text-red-600 truncate">
                {totalExpenses?.toLocaleString()} {schoolCurrency}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(TotalEarningsGraph);
